(() => {
  class CalendarTransportError extends Error {}
  const retryPause = (signal) => new Promise((resolve) => {
    if (signal.aborted) { resolve(); return; }
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    };
    const timer = setTimeout(finish, 750);
    signal.addEventListener("abort", finish, { once: true });
  });
  const escape = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  const dateKey = (year, month, day) =>
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const osloToday = () => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en", {
        timeZone: "Europe/Oslo",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value]),
    );
    return `${parts.year}-${parts.month}-${parts.day}`;
  };
  const safeLink = (value) => {
    if (!value) return "";
    try {
      const url = new URL(value, location.href);
      return url.protocol === "https:" ||
        (url.origin === location.origin && /^[/#]/.test(value))
        ? url.href
        : "";
    } catch {
      return "";
    }
  };
  class OchayaEventCalendar extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.locale = /^(nb|nn|no)(-|$)/.test(document.documentElement.lang)
          ? "nb-NO"
          : "en-GB";
        const norwegian = this.locale === "nb-NO";
        this.copy = norwegian
          ? {
              upcoming: "Kommende",
              past: "Tidligere",
              group: "Vis kurs",
              date: "Velg en dato",
              empty: "Ingen kurs denne dagen.",
              noUpcoming: "Ingen kommende kurs er publisert ennå.",
              noPast: "Ingen tidligere kurs er registrert ennå.",
              event: "kurs",
              events: "kurs",
              ended: "Tidligere kurs",
              today: "I dag",
              recap: "Les om kurset",
              register: "Meld deg på",
              loading: "Henter kurskalenderen …",
              failed: "Kurskalenderen kunne ikke lastes. Prøv igjen om litt.",
              retry: "Prøv igjen",
            }
          : {
              upcoming: "Upcoming",
              past: "Past courses",
              group: "Course view",
              date: "Select a date",
              empty: "No courses on this date.",
              noUpcoming: "No upcoming courses have been published yet.",
              noPast: "No past courses have been recorded yet.",
              event: "course",
              events: "courses",
              ended: "Past course",
              today: "Today",
              recap: "Read the recap",
              register: "Register",
              loading: "Loading the course calendar …",
              failed: "The course calendar could not be loaded. Please try again shortly.",
              retry: "Try again",
            };
        this.monthFormatter = new Intl.DateTimeFormat(this.locale, {
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        });
        this.dateFormatter = new Intl.DateTimeFormat(this.locale, {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        });
        this.events = this.readEvents();
        this.mode = "upcoming";
        this.today = osloToday();
        this.grid = this.querySelector("[data-calendar-grid]");
        this.details = this.querySelector("[data-calendar-details]");
        this.prev = this.querySelector("[data-calendar-prev]");
        this.next = this.querySelector("[data-calendar-next]");
        this.retryButton = this.querySelector("[data-calendar-retry]");
        if (this.retryButton) {
          this.retryButton.textContent = this.copy.retry;
          this.retryButton.addEventListener("click", () => this.loadFeed(true));
        }
        this.prev?.addEventListener("click", () => this.stepMonth(-1));
        this.next?.addEventListener("click", () => this.stepMonth(1));
        this.querySelector("[data-calendar-view-controls]")?.setAttribute(
          "aria-label",
          this.copy.group,
        );
        this.querySelectorAll("[data-calendar-view]").forEach((button) => {
          button.textContent = this.copy[button.dataset.calendarView];
          button.addEventListener("click", () => {
            this.mode = button.dataset.calendarView;
            this.resetView();
          });
        });
        this.grid?.addEventListener("click", (event) => {
          const button = event.target.closest("[data-date-key]");
          if (!button || button.disabled) return;
          this.selected = button.dataset.dateKey;
          this.render();
          this.grid
            .querySelector(`[data-date-key="${this.selected}"]`)
            ?.focus({ preventScroll: true });
        });
        const weekday = new Intl.DateTimeFormat(this.locale, {
          weekday: "short",
          timeZone: "UTC",
        });
        this.querySelector("[data-calendar-weekdays]").innerHTML = Array.from(
          { length: 7 },
          (_, i) =>
            `<div class="ochaya-event-calendar__weekday">${escape(weekday.format(new Date(Date.UTC(2024, 0, 1 + i))))}</div>`,
        ).join("");
        this.refresh = () => {
          if (["sheets", "private_sheets"].includes(this.dataset.contentSource) && !document.hidden &&
              (!this.lastFetch || Date.now() - this.lastFetch >= 300000)) this.loadFeed();
          const today = osloToday();
          if (today !== this.today) {
            this.today = today;
            this.resetView();
          }
        };
        this.resetView();
      }
      this.refresh();
      clearInterval(this.clock);
      this.clock = setInterval(this.refresh, 60000);
      document.addEventListener("visibilitychange", this.refresh);
    }
    disconnectedCallback() {
      clearInterval(this.clock);
      this.feedAbort?.abort();
      document.removeEventListener("visibilitychange", this.refresh);
    }
    // Both feeds are public projections. Never attach Google account cookies.
    async loadFeed(manualRetry = false) {
      if (this.fetching || !this.isConnected) return;
      this.fetching = true;
      this.manualRetry = manualRetry;
      this.lastFetch = Date.now();
      this.feedState = "loading";
      this.events = [];
      this.resetView();
      const run = new AbortController();
      this.feedAbort = run;
      const privateCalendar = this.dataset.contentSource === "private_sheets";
      const attempts = privateCalendar ? 2 : 1;
      try {
        for (let attempt = 0; attempt < attempts; attempt++) {
          if (run.signal.aborted) throw Error("Calendar request cancelled");
          const controller = new AbortController();
          const cancel = () => controller.abort();
          run.signal.addEventListener("abort", cancel, { once: true });
          // Preserve the original private-reader deadline; Apps Script can take >10s.
          const timeout = setTimeout(cancel, privateCalendar ? 20000 : 10000);
          try {
            const url = new URL(this.dataset.calendarFeed);
            let nonce;
            if (privateCalendar) {
              if (url.origin !== "https://script.google.com" ||
                  !/^\/macros\/s\/[A-Za-z0-9_-]+\/exec$/.test(url.pathname)) throw Error("Invalid calendar endpoint");
              nonce = crypto.randomUUID();
              url.search = "";
              url.searchParams.set("route", "calendar");
              url.searchParams.set("format", "json");
              url.searchParams.set("nonce", nonce);
              url.searchParams.set("lang", this.locale === "en-GB" ? "en" : "nb");
            } else if (url.origin !== "https://docs.google.com" ||
                !/^\/spreadsheets\/d\/e\/[A-Za-z0-9_-]+\/pub$/.test(url.pathname) ||
                url.searchParams.get("output") !== "csv" ||
                !/^\d+$/.test(url.searchParams.get("gid") || "") ||
                url.searchParams.get("single") !== "true") throw Error("Invalid calendar URL");
            let response;
            try {
              response = await fetch(url.href, {signal: controller.signal, credentials: "omit", mode: "cors"});
            } catch { throw new CalendarTransportError("Calendar network request failed"); }
            let temporaryGoogleEcho = false;
            if (privateCalendar && response.status === 404 && response.redirected === true) {
              try {
                const destination = new URL(response.url);
                temporaryGoogleEcho = destination.origin === "https://script.googleusercontent.com" &&
                  destination.pathname === "/macros/echo" && !destination.username && !destination.password;
              } catch { /* An invalid redirect URL remains a terminal response. */ }
            }
            if ((response.status >= 500 && response.status <= 599) || temporaryGoogleEcho)
              throw new CalendarTransportError("Calendar service unavailable");
            if (!response.ok || Number(response.headers.get("content-length")) > 1000000)
              throw Error("Calendar unavailable");
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let content = "", size = 0;
            while (true) {
              let chunk;
              try { chunk = await reader.read(); }
              catch { throw new CalendarTransportError("Calendar response interrupted"); }
              const {value, done} = chunk;
              if (done) break;
              size += value.byteLength;
              if (size > 1000000) { await reader.cancel(); throw Error("Calendar too large"); }
              content += decoder.decode(value, {stream: true});
            }
            content += decoder.decode();
            if (run.signal.aborted) throw Error("Calendar request cancelled");
            if (privateCalendar) {
              const data = JSON.parse(content);
              if (data?.type !== "ochaya-calendar" || data.nonce !== nonce || data.ok !== true ||
                  !Array.isArray(data.events) || data.events.length > 1000) throw Error("Invalid calendar response");
              this.events = this.readEvents(data.events);
            } else {
              this.events = this.readEvents(this.parseFeed(content));
            }
            this.feedState = "ready";
            break;
          } catch (error) {
            if (!(error instanceof CalendarTransportError) || attempt + 1 === attempts || run.signal.aborted)
              throw error;
          } finally {
            clearTimeout(timeout);
            run.signal.removeEventListener("abort", cancel);
          }
          await retryPause(run.signal);
        }
      } catch {
        this.events = [];
        this.feedState = "failed";
      } finally {
        this.fetching = false;
        if (this.isConnected) this.resetView();
      }
    }
    parseFeed(csv) {
      // RFC 4180 fields, including escaped quotes, newlines, and UTF-8 BOM.
      const rows = [], row = [];
      let field = "", quoted = false, closed = false;
      csv = csv.replace(/^\uFEFF/, "");
      for (let i = 0; i < csv.length; i++) {
        const c = csv[i];
        if (quoted) {
          if (c === '"' && csv[i + 1] === '"') { field += '"'; i++; }
          else if (c === '"') { quoted = false; closed = true; }
          else field += c;
        } else if (c === '"' && !field && !closed) quoted = true;
        else if (c === "," || c === "\n" || c === "\r") {
          row.push(field); field = ""; closed = false;
          if (c !== ",") {
            if (c === "\r" && csv[i + 1] === "\n") i++;
            rows.push(row.splice(0));
          }
        } else {
          if (closed || c === '"') throw Error("Malformed CSV");
          field += c;
        }
        if (rows.length > 5000 || row.length > 20 || field.length > 10000) throw Error("Calendar limits exceeded");
      }
      if (quoted) throw Error("Unclosed CSV field");
      if (field || row.length || closed) { row.push(field); rows.push(row); }
      const expected = ["date", "title", "time", "venue", "description", "registration_url", "recap_url", "image", "title_en", "description_en"];
      if (JSON.stringify(rows.shift()) !== JSON.stringify(expected)) throw Error("Unexpected calendar columns");
      return rows.filter(r => r.some(Boolean)).map((r, index) => {
        if (r.length !== expected.length || !/^\d{4}-\d{2}-\d{2}$/.test(r[0])) throw Error("Invalid calendar row");
        const [year, month, day] = r[0].split("-").map(Number);
        const english = this.locale !== "nb-NO";
        return {id: `sheet-${index}`, year, month, day, published: true,
          title: english && r[8] ? r[8] : r[1], subtitle: [r[2], r[3]].filter(Boolean).join(" · "),
          description: english && r[9] ? r[9] : r[4], link: r[5], archiveLink: r[6],
          image: r[7], linkLabel: this.copy.register};
      });
    }
    readEvents(input) {
      try {
        const fromSnapshot = input === undefined && this.dataset.contentSource === "shop_snapshot";
        let data = input ?? JSON.parse(
          this.querySelector("[data-calendar-events]")?.textContent || "[]",
        );
        if (fromSnapshot) {
          if (data?.schemaVersion !== 1 || typeof data.syncedAt !== "string" ||
              !Number.isFinite(Date.parse(data.syncedAt)) || new Date(data.syncedAt).toISOString() !== data.syncedAt ||
              !Array.isArray(data.nb) || !Array.isArray(data.en) ||
              data.nb.length > 1000 || data.en.length > 1000) throw Error("Invalid Shopify calendar copy");
          data = data[this.locale === "nb-NO" ? "nb" : "en"];
        }
        if (!Array.isArray(data)) return [];
        const events = data
          .filter((event) => {
            if (
              !event ||
              event.published === false ||
              typeof event.title !== "string" ||
              !event.title.trim()
            )
              return false;
            if (fromSnapshot && (event.published !== true ||
                ["id", "badge", "subtitle", "description", "linkLabel", "link", "archiveLink", "image", "imageAlt"]
                  .some(field => event[field] !== undefined && typeof event[field] !== "string"))) return false;
            const { year, month, day } = event;
            if (
              ![year, month, day].every(Number.isInteger) ||
              year < 2000 ||
              year > 2100
            )
              return false;
            const date = new Date(Date.UTC(year, month - 1, day));
            return (
              date.getUTCFullYear() === year &&
              date.getUTCMonth() === month - 1 &&
              date.getUTCDate() === day
            );
          })
          .map((event) => ({
            ...event,
            dateKey: dateKey(event.year, event.month, event.day),
          }))
          .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
        if (fromSnapshot && events.length !== data.length) throw Error("Invalid Shopify course record");
        return events;
      } catch {
        if (this.dataset.contentSource === "shop_snapshot") this.feedState = "failed";
        return [];
      }
    }
    resetView() {
      this.visibleEvents = this.events.filter((e) =>
        this.mode === "past" ? e.dateKey < this.today : e.dateKey >= this.today,
      );
      const keys = new Set(
        this.visibleEvents.map((e) => e.dateKey.slice(0, 7)),
      );
      // Start at the next real event (or latest historical month), including empty months between events.
      if (!keys.size) keys.add(this.today.slice(0, 7));
      const sorted = [...keys].sort();
      let [year, month] = sorted[0].split("-").map(Number);
      const end = sorted[sorted.length - 1];
      this.months = [];
      while (dateKey(year, month, 1).slice(0, 7) <= end) {
        this.months.push({ year, month });
        month += 1;
        if (month > 12) {
          year += 1;
          month = 1;
        }
      }
      this.monthIndex = this.mode === "past" ? this.months.length - 1 : 0;
      this.selected = null;
      this.render();
    }
    stepMonth(delta) {
      const index = this.monthIndex + delta;
      if (index < 0 || index >= this.months.length) return;
      this.monthIndex = index;
      this.selected = null;
      this.render();
    }
    render() {
      if (this.retryButton) {
        const focused = document.activeElement === this.retryButton;
        this.retryButton.hidden = this.feedState !== "failed" && !(this.manualRetry && this.feedState === "loading");
        this.retryButton.setAttribute("aria-disabled", String(this.feedState === "loading"));
        if (focused && this.retryButton.hidden)
          this.querySelector("[data-calendar-summary]").focus({ preventScroll: true });
      }
      const { year, month } = this.months[this.monthIndex];
      const selectedMonth = this.visibleEvents.filter(
        (e) => e.year === year && e.month === month,
      );
      if (!this.selected)
        this.selected =
          (this.mode === "past" ? selectedMonth.at(-1) : selectedMonth[0])
            ?.dateKey || null;
      this.querySelector("[data-calendar-month-label]").textContent =
        this.monthFormatter.format(new Date(Date.UTC(year, month - 1, 1)));
      this.prev.disabled = this.monthIndex === 0;
      this.next.disabled = this.monthIndex === this.months.length - 1;
      this.querySelectorAll("[data-calendar-view]").forEach((button) =>
        button.setAttribute(
          "aria-pressed",
          String(button.dataset.calendarView === this.mode),
        ),
      );
      if (this.feedState === "loading" || this.feedState === "failed") {
        this.querySelector("[data-calendar-summary]").textContent = this.copy[this.feedState];
        this.grid.innerHTML = "";
        this.details.textContent = this.copy[this.feedState];
        this.prev.disabled = this.next.disabled = true;
        return;
      }
      const count = this.visibleEvents.length;
      this.querySelector("[data-calendar-summary]").textContent = count
        ? `${this.copy[this.mode]} · ${count} ${count === 1 ? this.copy.event : this.copy.events}`
        : this.copy[this.mode === "past" ? "noPast" : "noUpcoming"];
      const leading =
        (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
      const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
      const previousDays = new Date(Date.UTC(year, month - 1, 0)).getUTCDate();
      const total = Math.ceil((leading + days) / 7) * 7;
      this.grid.innerHTML = Array.from({ length: total }, (_, i) => {
        const day = i - leading + 1;
        const outside = day < 1 || day > days;
        const key = dateKey(year, month, day);
        const events = outside
          ? []
          : selectedMonth.filter((e) => e.dateKey === key);
        const first = events[0];
        const label = outside
          ? day < 1
            ? previousDays + day
            : day - days
          : day;
        const accessible = outside
          ? String(label)
          : `${this.dateFormatter.format(new Date(Date.UTC(year, month - 1, day)))}${key === this.today ? `, ${this.copy.today}` : ""}${events.length ? `: ${events.map((e) => e.title).join("; ")}` : ""}`;
        return `<button type="button" class="ochaya-event-calendar__day${outside ? " is-outside-month" : ""}${events.length ? " is-event" : ""}${key === this.selected ? " is-selected" : ""}${key === this.today ? " is-today" : ""}" data-date-key="${key}" aria-label="${escape(accessible)}" aria-pressed="${key === this.selected}" ${outside ? "disabled" : ""}>
          <span class="ochaya-event-calendar__day-number">${label}</span><span class="ochaya-event-calendar__day-summary">${first?.badge ? `<span class="ochaya-event-calendar__day-tag">${escape(first.badge)}</span>` : ""}${first ? `<span class="ochaya-event-calendar__day-title">${escape(first.title)}${events.length > 1 ? ` +${events.length - 1}` : ""}</span>` : ""}</span></button>`;
      }).join("");
      this.renderDetails(
        selectedMonth.filter((e) => e.dateKey === this.selected),
      );
    }
    renderDetails(events) {
      if (!events.length) {
        const message = this.visibleEvents.length
          ? this.copy.empty
          : this.copy[this.mode === "past" ? "noPast" : "noUpcoming"];
        this.details.innerHTML = `<div class="ochaya-event-calendar__details-empty"><p class="ochaya-event-calendar__details-eyebrow">${escape(this.copy[this.mode])}</p><p class="ochaya-event-calendar__details-copy">${escape(message)}</p></div>`;
        return;
      }
      const { year, month, day } = events[0];
      this.details.innerHTML = `<div class="ochaya-event-calendar__detail-list"><p class="ochaya-event-calendar__details-eyebrow">${escape(this.dateFormatter.format(new Date(Date.UTC(year, month - 1, day))))}</p>${events
        .map((event) => {
          const past = event.dateKey < this.today;
          const link = safeLink(past ? event.archiveLink : event.link);
          const label = past ? this.copy.recap : (event.linkLabel || this.copy.register);
          const image = safeLink(event.image);
          return `<article class="ochaya-event-calendar__detail-item">${past ? `<p class="ochaya-event-calendar__event-badge">${escape(this.copy.ended)}</p>` : ""}${event.badge ? `<p class="ochaya-event-calendar__event-badge">${escape(event.badge)}</p>` : ""}<h3 class="ochaya-event-calendar__event-title">${escape(event.title)}</h3>${event.subtitle ? `<p class="ochaya-event-calendar__event-subtitle">${escape(event.subtitle)}</p>` : ""}${event.description ? `<p class="ochaya-event-calendar__event-description">${escape(event.description)}</p>` : ""}${link && label ? `<a class="ochaya-event-calendar__event-link" href="${escape(link)}">${escape(label)}</a>` : ""}${image ? `<img class="ochaya-event-calendar__event-image" src="${escape(image)}" alt="${escape(event.imageAlt || event.title)}" loading="lazy" width="900" height="600">` : ""}</article>`;
        })
        .join("")}</div>`;
    }
  }
  if (!customElements.get("ochaya-event-calendar"))
    customElements.define("ochaya-event-calendar", OchayaEventCalendar);
})();
